import axios from 'axios';
import { API_BASE } from '../config/api';
import { kc } from '../auth/keycloak';
import { ensureFreshToken, reportUnauthorized } from '../auth/sessionExpiry';

const client = axios.create({
  baseURL: API_BASE,
});

client.interceptors.request.use(async (config) => {
  await ensureFreshToken();
  config.headers.Authorization = `Bearer ${kc.token}`;
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    // Only a 401 can mean the session died, and even then usually does not, so
    // reportUnauthorized tests it rather than assuming. The error is rethrown
    // either way: every caller keeps the error handling it already had.
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      await reportUnauthorized();
    }
    return Promise.reject(error);
  },
);

export default client;

type ApiErrorDetail = {
  /**
   * A string for our own errors, but FastAPI's own 422 handler answers with an
   * array of objects — so this is `unknown` on purpose. Handing that array to a
   * component that renders it throws while rendering, which AppErrorBoundary
   * now catches -- but the member still gets an error page instead of the
   * screen they asked for, so the caller's own fallback message is the answer.
   */
  detail?: unknown;
};

// TODO: Consider to refactor the whole app, so that API client takes care of showing a toast with the error message and does the following check, instead of having to do it in every single component that makes an API call.

export const getApiErrorDetail = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) return undefined;
  const data = error.response?.data as ApiErrorDetail | undefined;
  return typeof data?.detail === 'string' ? data.detail : undefined;
};
